/**
 * ============================================================================
 * TRANSACTION CARD COMPONENT - 100% Ready
 * ============================================================================
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../providers';
import { getStatusLabel, getStatusColor, getStatusProgress } from '../../utils/transactionStatus';
import Card from '../common/Card';
import StatusBadge from '../common/StatusBadge';
import ProgressBar from '../common/ProgressBar';
import Avatar from '../common/Avatar';

/**
 * Transaction Card Component
 * @param {Object} props
 * @param {Object} props.transaction - Transaction object
 * @param {Function} props.onClick - Click handler
 * @returns {JSX.Element}
 */
const TransactionCard = ({ transaction, onClick }) => {
  const navigate = useNavigate();
  const { formatCurrency, formatDate, language } = useI18n();

  if (!transaction) return null;

  const statusLabel = getStatusLabel(transaction.status);
  const statusColor = getStatusColor(transaction.status);
  const progress = getStatusProgress(transaction.status);

  const handleClick = () => {
    if (onClick) {
      onClick(transaction);
    } else {
      navigate(`/transactions/${transaction.id}`);
    }
  };

  const getStatusVariant = (status) => {
    const variantMap = {
      completed: 'success',
      delivered: 'info',
      in_progress: 'warning',
      disputed: 'error',
      cancelled: 'default',
      escrow_funded: 'success',
      payment_pending: 'warning',
    };
    return variantMap[status] || 'default';
  };

  return (
    <div
      className="bg-[#111] border border-[rgba(255,255,255,0.08)] rounded-xl p-6 cursor-pointer transition-all duration-300 hover:border-[rgba(34,197,94,0.35)] hover:shadow-[0_0_40px_rgba(34,197,94,0.12)] animate-fadeInUp"
      onClick={handleClick}
    >
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#22c55e] to-[#16a34a] flex items-center justify-center text-2xl shadow-lg shadow-[rgba(34,197,94,0.3)]">
                📦
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-black text-white mb-1">
                  {transaction.title || (language === 'fr' ? 'Transaction sans titre' : 'Untitled Transaction')}
                </h3>
                {(transaction.referenceId || transaction.shortCode) && (
                  <p className="text-xs text-[#a3a3a3] font-mono">
                    #{transaction.referenceId || transaction.shortCode}
                  </p>
                )}
              </div>
            </div>
          </div>
          <StatusBadge variant={getStatusVariant(transaction.status)} size="sm">
            {statusLabel}
          </StatusBadge>
        </div>

        {/* Progress Bar */}
        {progress > 0 && (
          <ProgressBar value={progress} variant="info" size="sm" animated />
        )}

        {/* Amount */}
        <div className="flex items-center justify-between p-3 bg-[rgba(34,197,94,0.12)] border border-[rgba(34,197,94,0.2)] rounded-xl">
          <span className="text-sm font-medium text-[#a3a3a3]">
            {language === 'fr' ? 'Montant' : 'Amount'}
          </span>
          <span className="text-xl font-black text-[#22c55e]">
            {formatCurrency(transaction.amount || 0, transaction.currency || 'XAF')}
          </span>
        </div>

        {/* Details */}
        <div className="space-y-2 text-sm">
          {transaction.createdAt && (
            <div className="flex justify-between items-center">
              <span className="text-[#a3a3a3]">
                {language === 'fr' ? 'Créé' : 'Created'}
              </span>
              <span className="font-medium text-white">
                {formatDate(transaction.createdAt)}
              </span>
            </div>
          )}

          {transaction.deliveryDate && (
            <div className="flex justify-between items-center">
              <span className="text-[#a3a3a3]">
                {language === 'fr' ? 'Livraison' : 'Delivery'}
              </span>
              <span className="font-medium text-white">
                {formatDate(transaction.deliveryDate)}
              </span>
            </div>
          )}
        </div>

        {/* Description */}
        {transaction.description && (
          <p className="text-sm text-[#a3a3a3] line-clamp-2">
            {transaction.description}
          </p>
        )}

        {/* Parties */}
        {(transaction.creator || transaction.counterparty) && (
          <div className="flex items-center gap-2 pt-3 border-t border-[rgba(255,255,255,0.08)]">
            <Avatar
              name={transaction.creator?.name || transaction.counterparty?.name || 'U'}
              size="sm"
            />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-white truncate">
                {transaction.creator?.name || transaction.counterparty?.name || (language === 'fr' ? 'Partie' : 'Party')}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TransactionCard;
